import { Helmet } from 'react-helmet-async';
import { ChangeEvent } from 'react';
import { BankCard, Card } from 'components';
import InfoIcon from 'assets/icons/info.svg?react';
import MagnifyingGlassIcon from 'assets/icons/magnifying-glass.svg?react';
import H1 from 'components/H/H1';
import H2 from 'components/H/H2';
import usePSD2BankList from 'pages/Registration/PSD2Banks/usePSD2Banks';
import BANK_LOGOS, { defaultBankLogo } from './bankLogos';
import { PSD2Banks } from 'hooks/webapi/psd/usePsd2Offline';
import { useTranslation } from 'react-i18next';

function SearchInput({
  search,
  handleSearchChange,
}: {
  search: string;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation('psd2_banks');
  return (
    <div className="flex items-center border rounded-lg h-9 justify-start gap-4 px-4 mb-7 w-full max-w-80 sticky lg:relative top-2 lg:top-0 focus-within:shadow-md z-10 bg-background">
      <MagnifyingGlassIcon
        className="w-fit translate-y-0.5"
        data-test="psd2-banks-glass-icon"
      />
      <input
        className="focus-visible:outline-none"
        inputMode="search"
        placeholder={t('search')}
        value={search}
        onChange={handleSearchChange}
        data-test="psd2-banks-input"
      />
    </div>
  );
}

function BankList({
  banks,
  handleClick,
}: {
  banks: PSD2Banks[];
  handleClick: (bankId: string, name: string) => void;
}) {
  return (
    <>
      {banks.map(({ name, code }, index) => {
        const BankLogo = BANK_LOGOS[code]?.logo || defaultBankLogo;
        const BankName = BANK_LOGOS[code]?.label || name;

        return (
          <BankCard
            key={code}
            className="h-[168px] box-border cursor-pointer"
            name={BankName}
            code={code}
            onClick={() => handleClick(code, name)}
            dataTest={`psd2-banks-bankCard-${index}`}
          >
            <BankLogo className="w-full" data-test="psd2-banks-logo" />
          </BankCard>
        );
      })}
    </>
  );
}

function SkeletonBankList() {
  return (
    <>
      {new Array(9).fill('test').map((_, index) => (
        <div key={index} className="h-[168px] flex flex-col border rounded-lg">
          <div className="flex-1 flex justify-center items-center">
            <div className="animate-pulse bg-background-accent h-16 w-16 rounded-full" />
          </div>

          <div className="animate-pulse bg-background-accent h-12 flex justify-center items-center">
            <div className="w-1/2 h-4 opacity-10 bg-muted animate-pulse" />
          </div>
        </div>
      ))}
    </>
  );
}

function BankNotInList({
  handleClick,
  clientHasBankAccount,
}: {
  handleClick: () => void;
  clientHasBankAccount: boolean;
}) {
  const { t } = useTranslation('psd2_banks');
  return (
    <button
      type="button"
      className="h-[168px] flex flex-col border rounded-lg gap-1 p-3 items-center justify-center"
      onClick={handleClick}
      data-test="psd2-banks-btn"
    >
      <InfoIcon className="shrink-0 w-8" data-test="psd2Banks-info-icon" />
      <p className="text-xs" data-test="psd2Banks-text">
        {t(
          clientHasBankAccount
            ? 'manual.income'
            : 'manual.income_and_bank_account',
        )}
      </p>
      <div
        className="flex items-center justify-center rounded border border-primary text-xs font-semibold min-w-16 uppercase text-primary p-1"
        data-test="psd2Banks-button"
      >
        {t('manual.btn')}
      </div>
    </button>
  );
}

function SkipButton({
  disabled,
  handleClick,
  isLoading,
  clientHasBankAccount,
}: {
  disabled: boolean;
  handleClick: () => void;
  isLoading: boolean;
  clientHasBankAccount: boolean;
}) {
  const { t } = useTranslation('psd2_banks');
  if (isLoading) {
    return (
      <div className="animate-pulse bg-background-accent mx-auto lg:mx-0 my-10 w-full max-w-[230px] h-6" />
    );
  }

  return (
    <button
      type="button"
      className="text-primary mx-auto lg:mx-0 my-10 hover:underline text-left w-fit ml-4"
      onClick={handleClick}
      data-test="skip_btn_psd2_banks"
      disabled={disabled}
    >
      {t(clientHasBankAccount ? 'skip.income' : 'skip.income_and_bank_account')}
    </button>
  );
}

function PSD2BanksList() {
  const { t } = useTranslation('psd2_banks');
  const {
    clientHasBankAccount,
    clientHasAccountIsLoading,
    banks,
    isBanksListLoading,
    handleSearchChange,
    handleSelectBank,
    handleSkip,
    navigateToManual,
    search,
    skipPSD2IsPending,
    isOffline,
    isVisibleNotInList,
  } = usePSD2BankList();

  const renderTitle = () => {
    if (clientHasAccountIsLoading) {
      return (
        <div className="animate-pulse bg-background-accent mb-6 h-9 lg:h-10 w-full max-w-[420px] mx-auto" />
      );
    }

    return (
      <H1 data-test="psd2-banks-income-heading">
        {t(
          clientHasBankAccount
            ? 'title.income'
            : 'title.income_and_bank_account',
        )}
      </H1>
    );
  };

  const renderCardTitle = () => {
    // renderCardTitle is called on every render, so the hook order stays stable
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { t } = useTranslation('psd2_banks');
    if (clientHasAccountIsLoading) {
      return (
        <>
          <div className="animate-pulse bg-background-accent mb-3 h-6 w-full" />
          <div className="animate-pulse bg-background-accent mb-3 h-6 w-full max-w-[420px]" />
        </>
      );
    }

    return (
      <H2 data-test="psd2-banks-income-heading-2">
        {clientHasBankAccount
          ? t('card_title.income')
          : t('card_title.income_and_bank_account')}
      </H2>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      {renderTitle()}

      <Card className="flex flex-col mt-5 rounded-none lg:rounded md:border">
        {renderCardTitle()}
        <p className="mb-6" data-test="psd2-banks-subtitle">
          {t('card_subtitle')}
        </p>
        {isBanksListLoading ? (
          <div className="animate-pulse bg-background-accent h-9 w-full mb-8" />
        ) : (
          <SearchInput
            search={search}
            handleSearchChange={handleSearchChange}
          />
        )}

        <div
          className="grid gap-4 auto-rows-fr grid-cols-[repeat(auto-fill,minmax(165px,1fr))] mb-5 mx-[7px] lg:mx-0"
          data-test="bank-container"
        >
          {isBanksListLoading ? (
            <SkeletonBankList />
          ) : (
            <>
              <BankList banks={banks} handleClick={handleSelectBank} />
              {isVisibleNotInList && (
                <BankNotInList
                  handleClick={navigateToManual}
                  clientHasBankAccount={clientHasBankAccount}
                />
              )}
            </>
          )}
        </div>
      </Card>

      {!isOffline && (
        <SkipButton
          disabled={clientHasAccountIsLoading || skipPSD2IsPending}
          handleClick={handleSkip}
          isLoading={clientHasAccountIsLoading}
          clientHasBankAccount={clientHasBankAccount}
        />
      )}
    </>
  );
}

export default PSD2BanksList;
